// Catalog.ID Client — Reprint selection, the pure half
// ============================================================================
// Turning "which sheets, how many of each" into the arguments `buildRecoveryKit`
// takes (registration-restructure §16.6).
//
// Two conventions meet here and neither may leak into the dialog:
//
//   - the kit builder counts ADDITIONAL copies, beyond the one it always prints
//     for a chosen sheet type — the member is asked for a total ("print 2 of
//     sheet C"), so the plan subtracts one;
//   - `cardExtraCopies` is indexed by share index − 1 and must be exactly n
//     long, whether or not a given type is being printed this time.
//
// Copies are never additional sheet TYPES: every copy of a type carries the
// identical share and counts as one sheet toward the threshold (Addendum §2.3).

import { MAX_CARD_EXTRA_COPIES } from '../crypto/recovery-kit';

/** Total printed copies of one sheet type, the master one included. */
export const MAX_SHEET_COPIES = MAX_CARD_EXTRA_COPIES + 1;

export interface ReprintSheetChoice {
    /** Print this sheet type at all. */
    include: boolean;
    /** How many copies of it — 1..{@link MAX_SHEET_COPIES}. Ignored when
     *  `include` is false. */
    copies: number;
}

export interface ReprintSelection {
    /** Share indexes to evaluate, ascending. */
    indexes: number[];
    /** ADDITIONAL copies per share index, one entry per sheet type in the set. */
    cardExtraCopies: number[];
}

/** Message shown when a supersecret opens a bag that holds no kit entry. */
export const NO_KIT_ENTRY_MESSAGE =
    'Your account holds no Recovery Kit, so there is no set to print from. Kits created before this '
    + 'feature existed cannot be reprinted — create a new kit, then distribute its sheets.';

export function planReprintSelection(choices: readonly ReprintSheetChoice[]): ReprintSelection {
    const indexes: number[] = [];
    const cardExtraCopies: number[] = [];

    choices.forEach((choice, i) => {
        if (!choice.include) {
            cardExtraCopies.push(0);
            return;
        }
        if (!Number.isInteger(choice.copies) || choice.copies < 1 || choice.copies > MAX_SHEET_COPIES) {
            throw new Error(
                `Each chosen sheet may be printed between 1 and ${MAX_SHEET_COPIES} times `
                + '(copies of one sheet carry the same share and count as one sheet toward the threshold)');
        }
        indexes.push(i + 1);
        cardExtraCopies.push(choice.copies - 1);
    });

    if (indexes.length === 0) throw new Error('Choose at least one sheet to print');
    return { indexes, cardExtraCopies };
}
